import React, { useEffect, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { Button, Space, message } from 'antd';
import { jsPDF } from 'jspdf';
import { useAuth0 } from '@auth0/auth0-react';
import { LoadingOutlined } from '@ant-design/icons';

interface Empresa {
  id: number;
  denominacion: string;
  direccion: string;
  localidad: string;
  provincia: string;
}

interface Sucursal {
  id: number;
  descripcion: string;
  localidad: string;
  provincia: string;
}

interface Rollo {
  id: number;
  rollo_identificacion: string;
  tipo: string;
  alta: number;
}

interface PesajeData {
  numero_ticket: number;
  fecha_ticket: string;
  dtv: string;
  numero_remito: string;
  ingreso_egreso: string;
  compra_servicio: string;
  cliente_nombre: string;
  cliente_cuit: string;
  productor_nombre: string;
  productor_cuit: string;
  transportista_nombre: string;
  transportista_cuit: string;
  chofer_nombre: string;
  chofer_cuit: string;
  chofer_carnet: string;
  camion_patente_camion: string;
  camion_patente_acoplado: string;
  producto_nombre: string;
  kilos_primera: number;
  kilos_segunda: number;
  hora_primera: string;
  hora_segunda: string;
  humedad: number;
  establecimiento_nombre: string;
  nlote_nombre: string;
  variedad_semilla_nombre: string;
  presentacion_nombre: string;
  cantidad_rollos: number;
  puntaje_presentacion: number;
  ubicacion: string;
}

interface DataState {
  empresa: Empresa;
  sucursal: Sucursal;
  pesaje: PesajeData;
  rollos: Rollo[];
}

const ImprimirTicketBalanza: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const { user, getAccessTokenSilently, isLoading: authLoading } = useAuth0();
  const empresa = user?.id_empresa;
  const sucursal = user?.id_sucursal;
  
  console.log('Parámetros iniciales:', {
    id,
    pathName: location.pathname,
    empresa,
    sucursal
  });

  const [data, setData] = useState<DataState>({
    empresa: {} as Empresa,
    sucursal: {} as Sucursal,
    pesaje: {} as PesajeData,
    rollos: [],
  });
  const [loading, setLoading] = useState(false);
  const [isDataLoading, setIsDataLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      if (authLoading) return;
      
      console.log('Iniciando fetchData con:', {
        id,
        empresa,
        sucursal,
        authLoading
      });
      
      if (!empresa || !sucursal || !id) {
        console.error('Faltan datos necesarios:', { empresa, sucursal, id });
        message.error('Faltan datos necesarios para obtener la información');
        setIsDataLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        const token = await getAccessTokenSilently();
        const ruta = `${process.env.REACT_APP_BALANZA_URL}/imprimir_ticket/${empresa}/${sucursal}/${id}`;
        
        console.log('Intentando fetch a:', ruta);

        const response = await fetch(ruta, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
        });

        console.log('Status respuesta:', response.status);
        const jsonData = await response.json();
        console.log('Datos recibidos:', jsonData);

        if (!response.ok) {
          throw new Error(`Error HTTP: ${response.status} - ${jsonData.message || 'Error desconocido'}`);
        }

        if (jsonData.status === 'success' && jsonData.data) {
          console.log('Datos válidos recibidos:', jsonData.data);
          setData(jsonData.data);
        } else {
          throw new Error('Formato de respuesta inválido');
        }
      } catch (error: any) {
        console.error('Error detallado:', error);
        message.error(`Error al obtener los datos: ${error?.message || 'Error desconocido'}`);
      } finally {
        setLoading(false);
        setIsDataLoading(false);
      }
    };

    fetchData();
  }, [id, authLoading, empresa, sucursal, getAccessTokenSilently]);

  // Debug render
  console.log('Estado actual de data:', data);

  const exportToPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const leftMargin = 20;
    const rightMargin = pageWidth - 20;
    const lineHeight = 8;
    let y = 30;

    // 1. ENCABEZADO - Empresa y Sucursal
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text(data.empresa?.denominacion || 'Empresa', pageWidth/2, y, { align: 'center' });
    y += lineHeight;
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`${data.empresa?.direccion || ''} - ${data.empresa?.localidad || ''} - ${data.empresa?.provincia || ''}`, pageWidth/2, y, { align: 'center' });
    y += lineHeight;
    
    doc.setFont('helvetica', 'bold');
    doc.text(`Sucursal: ${data.sucursal?.descripcion || ''}, ${data.sucursal?.localidad || ''} - ${data.sucursal?.provincia || ''}`, pageWidth/2, y, { align: 'center' });
    y += lineHeight * 2;

    // 2. TÍTULO PRINCIPAL
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(`COMPROBANTE DE PESAJE BALANZA Nº ${data.pesaje?.numero_ticket || ''}`, pageWidth/2, y, { align: 'center' });
    y += lineHeight;
    
    // Tipo de operación
    const getTipoOperacion = (tipo: string) => {
      const tipos = {
        'C': 'COMPRA',
        'S': 'SERVICIO',
        'V': 'VENTA',
        'A': 'A OTRA SUCURSAL',
        'D': 'CAMBIO DUEÑO',
        'E': 'ENTREGA A CLIENTE',
        'B': 'BAJA',
        'F': 'DESDE SUCURSAL',
        'R': 'RECUPERO'
      };
      return tipos[tipo as keyof typeof tipos] || tipo;
    };
    
    doc.setFontSize(12);
    doc.text(getTipoOperacion(data.pesaje?.compra_servicio || ''), pageWidth/2, y, { align: 'center' });
    y += lineHeight;
    doc.setFont('helvetica', 'bold');
    doc.text(data.pesaje?.producto_nombre || '', pageWidth/2, y, { align: 'center' });
    y += lineHeight * 2;

    // 3. INFORMACIÓN PRINCIPAL - Dos columnas
    const colLeftX = leftMargin;
    const colRightX = pageWidth/2 + 20;
    let yLeft = y;
    let yRight = y;

    // Columna izquierda: CLIENTE, PRODUCTOR, TRANSPORTISTA
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text("CLIENTE", colLeftX, yLeft);
    yLeft += lineHeight;
    doc.setFont('helvetica', 'normal');
    doc.text(data.pesaje?.cliente_nombre || '', colLeftX, yLeft);
    yLeft += lineHeight;
    doc.text(data.pesaje?.cliente_cuit || '', colLeftX, yLeft);
    yLeft += lineHeight * 2;

    doc.setFont('helvetica', 'bold');
    doc.text("PRODUCTOR", colLeftX, yLeft);
    yLeft += lineHeight;
    doc.setFont('helvetica', 'normal');
    doc.text(data.pesaje?.productor_nombre || '', colLeftX, yLeft);
    yLeft += lineHeight;
    doc.text(data.pesaje?.productor_cuit || '', colLeftX, yLeft);
    yLeft += lineHeight;
    
    if (data.pesaje?.establecimiento_nombre && data.pesaje.establecimiento_nombre !== 'Sin establecimiento') {
      doc.text(`Establecimiento ${data.pesaje.establecimiento_nombre}`, colLeftX, yLeft);
      yLeft += lineHeight;
    }
    
    if (data.pesaje?.nlote_nombre && data.pesaje.nlote_nombre !== 'Sin lote') {
      doc.text(`Lote ${data.pesaje.nlote_nombre}`, colLeftX, yLeft);
      yLeft += lineHeight;
    }
    yLeft += lineHeight;

    doc.setFont('helvetica', 'bold');
    doc.text("TRANSPORTISTA", colLeftX, yLeft);
    yLeft += lineHeight;
    doc.setFont('helvetica', 'normal');
    doc.text(data.pesaje?.transportista_nombre || '', colLeftX, yLeft);
    yLeft += lineHeight;
    doc.text(data.pesaje?.transportista_cuit || '', colLeftX, yLeft);
    yLeft += lineHeight;
    doc.text(`CHOFER ${data.pesaje?.chofer_nombre || ''}`, colLeftX, yLeft);
    yLeft += lineHeight;
    doc.text(`CUIT: ${data.pesaje?.chofer_cuit || ''}`, colLeftX, yLeft);
    yLeft += lineHeight;
    doc.text(`CARNET: ${data.pesaje?.chofer_carnet || ''}`, colLeftX, yLeft);
    yLeft += lineHeight;
    doc.text(`CAMION ${data.pesaje?.camion_patente_camion || ''}`, colLeftX, yLeft);
    yLeft += lineHeight;
    doc.text(`ACOPLADO ${data.pesaje?.camion_patente_acoplado || ''}`, colLeftX, yLeft);

    // Columna derecha: Datos técnicos
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text(`DTV: ${data.pesaje?.dtv || ''}`, colRightX, yRight);
    yRight += lineHeight;
    doc.text(`REMITO: ${data.pesaje?.numero_remito || ''}`, colRightX, yRight);
    yRight += lineHeight;
    doc.text(`VARIEDAD SEMILLA: ${data.pesaje?.variedad_semilla_nombre || ''}`, colRightX, yRight);
    yRight += lineHeight;
    doc.text(`PRESENTACIÓN: ${data.pesaje?.presentacion_nombre || ''}`, colRightX, yRight);
    yRight += lineHeight;
    doc.text(`HUMEDAD: ${data.pesaje?.humedad || 0}`, colRightX, yRight);
    yRight += lineHeight;
    doc.text(`CANTIDAD ROLLOS/MÓDULOS: ${data.pesaje?.cantidad_rollos || 0}`, colRightX, yRight);
    yRight += lineHeight;
    doc.text(`PUNTAJE PRESENTACIÓN: ${data.pesaje?.puntaje_presentacion || 0}`, colRightX, yRight);
    yRight += lineHeight;
    doc.text(`UBICACIÓN EN PLAYA: ${data.pesaje?.ubicacion || ''}`, colRightX, yRight);

    // 4. DATOS DE PESAJE
    y = Math.max(yLeft, yRight) + lineHeight * 2;
    
    // Línea separadora
    doc.setLineWidth(0.5);
    doc.line(leftMargin, y, rightMargin, y);
    y += lineHeight * 2;

    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text("DATOS DE PESAJE", pageWidth/2, y, { align: 'center' });
    y += lineHeight * 2;

    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    
    // Primera Pasada
    doc.text(`Primera Pasada: ${data.pesaje?.kilos_primera || 0} kg`, leftMargin, y);
    const horaPrimera = data.pesaje?.hora_primera ? new Date(data.pesaje.hora_primera).toLocaleString('es-AR') : '';
    doc.text(`Hora: ${horaPrimera}`, rightMargin, y, { align: 'right' });
    y += lineHeight;

    // Segunda Pasada
    doc.text(`Segunda Pasada: ${data.pesaje?.kilos_segunda || 0} kg`, leftMargin, y);
    const horaSegunda = data.pesaje?.hora_segunda ? new Date(data.pesaje.hora_segunda).toLocaleString('es-AR') : '';
    doc.text(`Hora: ${horaSegunda}`, rightMargin, y, { align: 'right' });
    y += lineHeight;

    // Neto
    doc.setFont('helvetica', 'bold');
    const pesoNeto = Math.abs((data.pesaje?.kilos_primera || 0) - (data.pesaje?.kilos_segunda || 0));
    doc.text(`Neto: ${pesoNeto} kg`, leftMargin, y);
    y += lineHeight * 2;

    // 5. SECCIÓN MÓDULOS/ROLLOS
    if (data.rollos && data.rollos.length > 0) {
      // Verificar si necesitamos nueva página
      if (y > pageHeight - 100) {
        doc.addPage();
        y = 30;
      }

      // Línea separadora
      doc.setLineWidth(0.5);
      doc.line(leftMargin, y, rightMargin, y);
      y += lineHeight * 2;

      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text("MÓDULOS/ROLLOS", pageWidth/2, y, { align: 'center' });
      y += lineHeight * 2;

      // Tabla de rollos
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      
      // Encabezados
      doc.text("Nº", leftMargin, y);
      doc.text("Identificación", leftMargin + 30, y);
      doc.text("Tipo", leftMargin + 150, y);
      y += lineHeight;

      // Línea debajo de encabezados
      doc.setLineWidth(0.2);
      doc.line(leftMargin, y, rightMargin, y);
      y += lineHeight;

      // Datos de rollos
      doc.setFont('helvetica', 'normal');
      data.rollos.forEach((rollo, index) => {
        if (y > pageHeight - 30) {
          doc.addPage();
          y = 30;
        }
        
        doc.text((index + 1).toString(), leftMargin, y);
        doc.text(rollo.rollo_identificacion || '', leftMargin + 30, y);
        doc.text(rollo.tipo === 'A' ? 'Automático' : 'Manual', leftMargin + 150, y);
        y += lineHeight;
      });

      // Línea final de tabla
      doc.setLineWidth(0.2);
      doc.line(leftMargin, y, rightMargin, y);
      y += lineHeight * 2;
    }

    // 6. FECHAS
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    const fechaGeneracion = new Date().toLocaleDateString('es-AR');
    doc.text(`Fecha Generación: ${fechaGeneracion}`, rightMargin, y, { align: 'right' });
    y += lineHeight;
    
    const fechaEmision = data.pesaje?.fecha_ticket ? new Date(data.pesaje.fecha_ticket).toLocaleDateString('es-AR') : new Date().toLocaleDateString('es-AR');
    doc.text(`Fecha Emisión: ${fechaEmision}`, rightMargin, y, { align: 'right' });

    // Guardar PDF
    const nombreArchivo = `CI-${data.pesaje?.numero_ticket || 'sin_numero'}.pdf`;
    doc.save(nombreArchivo);
  };

  return (
    <div style={{ padding: '20px' }}>
      {isDataLoading ? (
        <div style={{ textAlign: 'center' }}>
          <LoadingOutlined style={{ fontSize: 24 }} spin />
          <p>Cargando datos... ID: {id}</p>
        </div>
      ) : (
        <>
          {/* Vista previa del ticket */}
          <div style={{ 
            maxWidth: '800px', 
            margin: '0 auto', 
            backgroundColor: 'white',
            border: '1px solid #d9d9d9',
            borderRadius: '8px',
            padding: '20px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            marginBottom: '20px'
          }}>
            {/* Encabezado */}
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: '0 0 10px 0', fontSize: '18px', fontWeight: 'bold' }}>
                {data.empresa?.denominacion || 'Empresa'}
              </h2>
              <p style={{ margin: '5px 0', fontSize: '12px' }}>
                {data.empresa?.direccion || ''} - {data.empresa?.localidad || ''} - {data.empresa?.provincia || ''}
              </p>
              <p style={{ margin: '5px 0', fontSize: '12px', fontWeight: 'bold' }}>
                Sucursal: {data.sucursal?.descripcion || ''}, {data.sucursal?.localidad || ''} - {data.sucursal?.provincia || ''}
              </p>
            </div>

            {/* Título principal */}
            <div style={{ textAlign: 'center', marginBottom: '20px', borderBottom: '2px solid #000', paddingBottom: '10px' }}>
              <h1 style={{ margin: '0', fontSize: '16px', fontWeight: 'bold' }}>
                COMPROBANTE DE PESAJE BALANZA Nº {data.pesaje?.numero_ticket || ''}
              </h1>
              <p style={{ margin: '5px 0', fontSize: '14px' }}>
                {data.pesaje?.compra_servicio === 'C' ? 'COMPRA' : 
                 data.pesaje?.compra_servicio === 'S' ? 'SERVICIO' :
                 data.pesaje?.compra_servicio === 'V' ? 'VENTA' :
                 data.pesaje?.compra_servicio || ''}
              </p>
              <p style={{ margin: '5px 0', fontSize: '14px', fontWeight: 'bold' }}>
                {data.pesaje?.producto_nombre || ''}
              </p>
            </div>

            {/* Información del cliente, productor, transportista y datos principales */}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
              <div style={{ flex: 1 }}>
                <h3 style={{ margin: '0 0 10px 0', fontSize: '14px', fontWeight: 'bold' }}>CLIENTE</h3>
                <p style={{ margin: '5px 0', fontSize: '12px' }}>{data.pesaje?.cliente_nombre || ''}</p>
                <p style={{ margin: '5px 0', fontSize: '12px' }}>{data.pesaje?.cliente_cuit || ''}</p>
                
                <h3 style={{ margin: '20px 0 10px 0', fontSize: '14px', fontWeight: 'bold' }}>PRODUCTOR</h3>
                <p style={{ margin: '5px 0', fontSize: '12px' }}>{data.pesaje?.productor_nombre || ''}</p>
                <p style={{ margin: '5px 0', fontSize: '12px' }}>{data.pesaje?.productor_cuit || ''}</p>
                {data.pesaje?.establecimiento_nombre && data.pesaje.establecimiento_nombre !== 'Sin establecimiento' && (
                  <p style={{ margin: '5px 0', fontSize: '12px' }}>Establecimiento {data.pesaje.establecimiento_nombre}</p>
                )}
                {data.pesaje?.nlote_nombre && data.pesaje.nlote_nombre !== 'Sin lote' && (
                  <p style={{ margin: '5px 0', fontSize: '12px' }}>Lote {data.pesaje.nlote_nombre}</p>
                )}
                
                <h3 style={{ margin: '20px 0 10px 0', fontSize: '14px', fontWeight: 'bold' }}>TRANSPORTISTA</h3>
                <p style={{ margin: '5px 0', fontSize: '12px' }}>{data.pesaje?.transportista_nombre || ''}</p>
                <p style={{ margin: '5px 0', fontSize: '12px' }}>{data.pesaje?.transportista_cuit || ''}</p>
                <p style={{ margin: '5px 0', fontSize: '12px' }}>CHOFER {data.pesaje?.chofer_nombre || ''}</p>
                <p style={{ margin: '5px 0', fontSize: '12px' }}>CUIT: {data.pesaje?.chofer_cuit || ''}</p>
                <p style={{ margin: '5px 0', fontSize: '12px' }}>CARNET: {data.pesaje?.chofer_carnet || ''}</p>
                <p style={{ margin: '5px 0', fontSize: '12px' }}>CAMION {data.pesaje?.camion_patente_camion || ''}</p>
                <p style={{ margin: '5px 0', fontSize: '12px' }}>ACOPLADO {data.pesaje?.camion_patente_acoplado || ''}</p>
              </div>
              <div style={{ flex: 1, textAlign: 'right' }}>
                <div style={{ marginBottom: '10px' }}>
                  <strong style={{ fontSize: '12px' }}>DTV:</strong> {data.pesaje?.dtv || ''}
                </div>
                <div style={{ marginBottom: '10px' }}>
                  <strong style={{ fontSize: '12px' }}>REMITO:</strong> {data.pesaje?.numero_remito || ''}
                </div>
                <div style={{ marginBottom: '10px' }}>
                  <strong style={{ fontSize: '12px' }}>VARIEDAD SEMILLA:</strong> {data.pesaje?.variedad_semilla_nombre || ''}
                </div>
                <div style={{ marginBottom: '10px' }}>
                  <strong style={{ fontSize: '12px' }}>PRESENTACIÓN:</strong> {data.pesaje?.presentacion_nombre || ''}
                </div>
                <div style={{ marginBottom: '10px' }}>
                  <strong style={{ fontSize: '12px' }}>HUMEDAD:</strong> {data.pesaje?.humedad || 0}
                </div>
                <div style={{ marginBottom: '10px' }}>
                  <strong style={{ fontSize: '12px' }}>CANTIDAD ROLLOS/MÓDULOS:</strong> {data.pesaje?.cantidad_rollos || 0}
                </div>
                <div style={{ marginBottom: '10px' }}>
                  <strong style={{ fontSize: '12px' }}>PUNTAJE PRESENTACIÓN:</strong> {data.pesaje?.puntaje_presentacion || 0}
                </div>
                <div style={{ marginBottom: '10px' }}>
                  <strong style={{ fontSize: '12px' }}>UBICACIÓN EN PLAYA:</strong> {data.pesaje?.ubicacion || ''}
                </div>
              </div>
            </div>

            {/* Datos de pesaje */}
            <div style={{ marginBottom: '20px', borderTop: '1px solid #d9d9d9', paddingTop: '15px' }}>
              <h3 style={{ textAlign: 'center', margin: '0 0 15px 0', fontSize: '14px', fontWeight: 'bold' }}>
                DATOS DE PESAJE
              </h3>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
                <div>
                  <strong>Primera Pasada:</strong> {data.pesaje?.kilos_primera || 0} kg
                </div>
                <div>
                  <strong>Hora:</strong> {data.pesaje?.hora_primera ? new Date(data.pesaje.hora_primera).toLocaleString('es-AR') : ''}
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginTop: '5px' }}>
                <div>
                  <strong>Segunda Pasada:</strong> {data.pesaje?.kilos_segunda || 0} kg
                </div>
                <div>
                  <strong>Hora:</strong> {data.pesaje?.hora_segunda ? new Date(data.pesaje.hora_segunda).toLocaleString('es-AR') : ''}
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginTop: '5px', fontWeight: 'bold' }}>
                <div>
                  <strong>Neto:</strong> {Math.abs((data.pesaje?.kilos_primera || 0) - (data.pesaje?.kilos_segunda || 0))} kg
                </div>
              </div>
            </div>

            {/* Sección de Módulos/Rollos */}
            {data.rollos && data.rollos.length > 0 && (
              <div style={{ marginBottom: '20px', borderTop: '1px solid #d9d9d9', paddingTop: '15px' }}>
                <h3 style={{ textAlign: 'center', margin: '0 0 15px 0', fontSize: '14px', fontWeight: 'bold' }}>
                  MÓDULOS/ROLLOS
                </h3>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #d9d9d9' }}>
                      <th style={{ textAlign: 'left', padding: '5px', borderBottom: '1px solid #d9d9d9' }}>Nº</th>
                      <th style={{ textAlign: 'left', padding: '5px', borderBottom: '1px solid #d9d9d9' }}>Identificación</th>
                      <th style={{ textAlign: 'left', padding: '5px', borderBottom: '1px solid #d9d9d9' }}>Tipo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.rollos.map((rollo, index) => (
                      <tr key={rollo.id}>
                        <td style={{ padding: '5px' }}>{index + 1}</td>
                        <td style={{ padding: '5px' }}>{rollo.rollo_identificacion || ''}</td>
                        <td style={{ padding: '5px' }}>{rollo.tipo === 'A' ? 'Automático' : 'Manual'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Fechas */}
            <div style={{ textAlign: 'right', marginTop: '20px', fontSize: '10px', color: '#666' }}>
              <p style={{ margin: '2px 0' }}>
                Fecha Generación: {new Date().toLocaleDateString('es-AR')}
              </p>
              <p style={{ margin: '2px 0' }}>
                Fecha Emisión: {data.pesaje?.fecha_ticket ? new Date(data.pesaje.fecha_ticket).toLocaleDateString('es-AR') : new Date().toLocaleDateString('es-AR')}
              </p>
            </div>
          </div>
          
          {/* Botones de acción */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center',
            gap: '10px',
            marginTop: '20px'
          }}>
            <Button 
              onClick={exportToPDF}
              loading={loading}
              disabled={!data.pesaje?.numero_ticket}
              type="primary"
              size="large"
            >
              Exportar a PDF
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default ImprimirTicketBalanza; 